import { EmailTemplateId, UserId, validEmail } from "@sp-crm/core";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import {
    GetWorkflowSettingsResult,
    useGetTaskTypesQuery,
    useGetTemplateQuery,
    WorkflowActorMode,
} from "generated/graphql";
import React, { useMemo, useState } from "react";
import { useUser } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import {
    EMAIL_RECIPIENT_TOKENS,
    hasSingleEmailAction,
    hasSingleTaskAction,
    resolveWorkflowSetting,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface WorkflowActionSummaryProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
}

interface ExpandableActionSummaryProps {
    summaryText: string;
    children: React.ReactNode;
}

const ExpandableActionSummary: React.FC<ExpandableActionSummaryProps> = ({
    summaryText,
    children,
}) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <span
                className="cursor-pointer inline-flex items-center gap-1"
                onClick={() => setExpanded(!expanded)}>
                {summaryText}
                {expanded ? (
                    <ChevronUpIcon className="w-4 h-4 text-gray-500" />
                ) : (
                    <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                )}
            </span>
            {expanded && (
                <dl className="mt-1 ml-4 text-gray-600 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                    {children}
                </dl>
            )}
        </>
    );
};

const UserName: React.FC<{ userId: UserId | null | undefined }> = ({ userId }) => {
    const user = useUser(userId);

    if (!user) {
        return <span>a specific user</span>;
    }

    return <span>{user.name}</span>;
};

const recipientTokens = [
    ...EMAIL_RECIPIENT_TOKENS,
    { label: "Referral contact", token: "{{client.referralContact}}" },
];

const resolveRecipientDescription = (recipientValue: string | null): string => {
    if (!recipientValue) {
        return "configured recipients";
    }

    const recipients = recipientValue.split(",").map(r => r.trim());

    const labels: string[] = [];
    const literalEmails: string[] = [];

    for (const recipient of recipients) {
        const tokenMatch = recipientTokens.find(t => t.token === recipient);
        if (tokenMatch) {
            labels.push(tokenMatch.label.toLowerCase());
        } else if (validEmail(recipient)) {
            literalEmails.push(recipient);
        }
    }

    const allLabels = [...labels, ...literalEmails];
    if (allLabels.length === 0) {
        return "Configured recipients";
    }

    const result = allLabels.join(", ");
    return result.charAt(0).toUpperCase() + result.slice(1);
};

interface EmailActionSummaryProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
}

const EmailActionSummary: React.FC<EmailActionSummaryProps> = ({
    workflow,
    workflowSettings,
}) => {
    const isAssignedUser = workflow.actorMode === WorkflowActorMode.AssignedUser;

    const templateId = useMemo(() => {
        return resolveWorkflowSetting(
            workflowSettings,
            workflow,
            "emailTemplate",
        ) as EmailTemplateId | null;
    }, [workflowSettings, workflow]);

    const getTemplate = useGetTemplateQuery(
        { id: templateId },
        { enabled: !!templateId },
    );

    const recipientValue = resolveWorkflowSetting(
        workflowSettings,
        workflow,
        "recipients",
    );
    const action = workflow.actions?.[0];

    let recipientDescription: string;
    if (recipientValue) {
        recipientDescription = resolveRecipientDescription(recipientValue);
    } else if (
        action?.__typename === "WorkflowActionEmailEntity" &&
        action.to === "{{client.referralContact}}"
    ) {
        recipientDescription = "Referral contact";
    } else {
        recipientDescription = "Configured recipients";
    }

    const templateSubject = getTemplate.data?.getTemplate?.template?.subject;
    const templateBody = getTemplate.data?.getTemplate?.template?.body;

    return (
        <ExpandableActionSummary
            summaryText={`Send email to ${recipientDescription.toLowerCase()}`}>
            <dt className="text-gray-500">To</dt>
            <dd>{recipientDescription}</dd>
            <dt className="text-gray-500">From</dt>
            <dd>
                {isAssignedUser ? (
                    "The client's assigned user"
                ) : (
                    <UserName userId={workflow.defaultActorId} />
                )}
            </dd>
            {templateSubject && (
                <>
                    <dt className="text-gray-500">Subject</dt>
                    <dd>{templateSubject}</dd>
                </>
            )}
            {templateBody ? (
                <>
                    <dt className="text-gray-500">Body</dt>
                    <dd>
                        <div
                            className="unreset rounded border border-gray-200 p-2 text-sm"
                            dangerouslySetInnerHTML={{ __html: templateBody }}
                        />
                    </dd>
                </>
            ) : null}
        </ExpandableActionSummary>
    );
};

interface TaskActionSummaryProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
}

const TaskActionSummary: React.FC<TaskActionSummaryProps> = ({
    workflow,
    workflowSettings,
}) => {
    const taskTypes = useGetTaskTypesQuery({}, stableQueryOptions());

    const action = workflow.actions?.[0];
    const taskTypeId =
        action?.__typename === "WorkflowActionTaskEntity" ? action.taskTypeId : null;

    const taskType = taskTypes.data?.getTaskTypes?.find(t => t.id === taskTypeId);

    const description = resolveWorkflowSetting(workflowSettings, workflow, "description");
    const cleanDescription = description?.trim() || null;

    const isAssignedUser = workflow.actorMode === WorkflowActorMode.AssignedUser;

    const summaryText = taskType?.name || cleanDescription || "task";

    return (
        <ExpandableActionSummary summaryText={`Create task: ${summaryText}`}>
            {taskType && (
                <>
                    <dt className="text-gray-500">Type</dt>
                    <dd>{taskType.name}</dd>
                </>
            )}
            {cleanDescription && (
                <>
                    <dt className="text-gray-500">Description</dt>
                    <dd>{cleanDescription}</dd>
                </>
            )}
            <dt className="text-gray-500">Assigned to</dt>
            <dd>
                {isAssignedUser ? (
                    "The client's assigned user"
                ) : (
                    <UserName userId={workflow.defaultActorId} />
                )}
            </dd>
        </ExpandableActionSummary>
    );
};

export const WorkflowActionSummary: React.FC<WorkflowActionSummaryProps> = ({
    workflow,
    workflowSettings,
}) => {
    if (hasSingleEmailAction(workflow)) {
        return (
            <EmailActionSummary workflow={workflow} workflowSettings={workflowSettings} />
        );
    }

    if (hasSingleTaskAction(workflow)) {
        return (
            <TaskActionSummary workflow={workflow} workflowSettings={workflowSettings} />
        );
    }

    return null;
};
